import { useLayoutEffect, useState } from "react";
import type { ImageRef } from "@/content/types";
import { resolvePublic } from "@/lib/media";

interface Props {
  image: ImageRef;
  className?: string;
  style?: React.CSSProperties;
  loading?: "eager" | "lazy";
  sizes?: string;
  /** Override the fallback used on image load error. When unset, MediaImage
   *  picks deterministically between two cat fallbacks based on alt-text hash. */
  fallbackKey?: string;
}

interface Fallback {
  key: string;
  /** object-position to apply when this fallback is rendered into a cropped
   *  container. Chosen per-image based on what part of the photo reads best. */
  objectPosition: string;
  /** Optional CSS transform applied along with the fallback (e.g. mirror). */
  transform?: string;
}

const FALLBACKS: ReadonlyArray<Fallback> = [
  // CAT 1 — composition leads with the cat's face near the top of the frame,
  // so a square crop should anchor to the top edge.
  { key: "public/fallbacks/cat-1.png", objectPosition: "center top" },
  // CAT 2 — reads best when the crop sits centered and the image is mirrored
  // horizontally (matches the visual balance of the rest of the site).
  { key: "public/fallbacks/cat-2.png", objectPosition: "center center", transform: "scaleX(-1)" },
];

/** Quick stable hash — used to pick a fallback per-image so a given release
 *  always shows the same cat rather than flickering between them. */
function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function pickFallback(seed: string): Fallback {
  return FALLBACKS[hashString(seed) % FALLBACKS.length]!;
}

const FADE_MS = 280;

/**
 * Render a public ImageRef as a <picture> with AVIF/WebP variants when available.
 * On <img> error, swaps to one of two fallback cat images chosen deterministically
 * by alt-text hash, applying object-position + optional transform so each crop
 * looks right in a card tile.
 *
 * Images fade in from opacity 0 → 1 on load (both primary and fallback) so
 * the appearance is smooth even when the image arrives a beat after page render.
 */
export function MediaImage({
  image,
  className,
  style,
  loading = "lazy",
  sizes,
  fallbackKey,
}: Props) {
  const [errored, setErrored] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const imageId = image.ref.kind === "external" ? image.ref.url : image.ref.key;

  // Reset both states before paint when the image changes — useEffect would
  // leave a one-frame flash of the previous load state on route transitions
  // (e.g. clicking Related → another release in the catalog).
  useLayoutEffect(() => {
    setErrored(false);
    setLoaded(false);
  }, [imageId]);

  const fallback: Fallback = fallbackKey
    ? { key: fallbackKey, objectPosition: "center center" }
    : pickFallback(image.alt);

  const transitionStyle: React.CSSProperties = {
    opacity: loaded ? 1 : 0,
    transition: `opacity ${FADE_MS}ms ease-out`,
  };

  const onLoad = () => setLoaded(true);
  const onError = () => {
    setErrored(true);
    // Reset loaded so the fallback fades in too rather than appearing instantly.
    setLoaded(false);
  };

  if (errored) {
    return (
      <img
        src={resolvePublic(fallback.key)}
        alt={image.alt}
        width={image.width}
        height={image.height}
        loading={loading}
        className={className}
        onLoad={onLoad}
        style={{
          height: "auto",
          display: "block",
          ...style,
          objectPosition: fallback.objectPosition,
          ...(fallback.transform ? { transform: fallback.transform } : {}),
          ...transitionStyle,
        }}
      />
    );
  }

  const styleWithDefaults: React.CSSProperties = {
    height: "auto",
    display: "block",
    ...style,
    ...transitionStyle,
  };

  if (image.ref.kind !== "public") {
    const src =
      image.ref.kind === "external" ? image.ref.url : resolvePublic(image.ref.key);
    return (
      <img
        src={src}
        alt={image.alt}
        width={image.width}
        height={image.height}
        loading={loading}
        className={className}
        onLoad={onLoad}
        onError={onError}
        style={styleWithDefaults}
      />
    );
  }

  const primaryFallback = resolvePublic(image.ref.key);
  const avif = image.variants?.filter((v) => v.format === "avif") ?? [];
  const webp = image.variants?.filter((v) => v.format === "webp") ?? [];

  return (
    <picture>
      {avif.length > 0 && (
        <source
          type="image/avif"
          srcSet={avif.map((v) => `${resolvePublic(v.key)} ${v.width}w`).join(", ")}
          sizes={sizes}
        />
      )}
      {webp.length > 0 && (
        <source
          type="image/webp"
          srcSet={webp.map((v) => `${resolvePublic(v.key)} ${v.width}w`).join(", ")}
          sizes={sizes}
        />
      )}
      <img
        src={primaryFallback}
        alt={image.alt}
        width={image.width}
        height={image.height}
        loading={loading}
        className={className}
        onLoad={onLoad}
        onError={onError}
        style={styleWithDefaults}
      />
    </picture>
  );
}
